/**
 * Drapeaux de visibilité des menus / routes « dashboard établissement ».
 * Stockés sur l’institution (`dashboardMenuFlags`). Absence de clé ou valeur !== false → activé.
 */

export const INSTITUTION_MENU_FLAGS = {
  institution_panel: 'institution_panel',
  integrations_page: 'integrations_page',
  tab_webinar_settings: 'tab_webinar_settings',
  tab_flows: 'tab_flows',
  tab_trash: 'tab_trash',
  tab_supports: 'tab_supports',
  tab_categories: 'tab_categories',
  tab_learners: 'tab_learners',
  tab_bi_studio: 'tab_bi_studio',
  tab_groups: 'tab_groups',
  tab_webhooks: 'tab_webhooks',
  tab_mcp: 'tab_mcp',
  tab_billing: 'tab_billing',
} as const;

export type InstitutionMenuFlagKey = (typeof INSTITUTION_MENU_FLAGS)[keyof typeof INSTITUTION_MENU_FLAGS];

export const ALL_INSTITUTION_MENU_FLAG_KEYS = Object.values(INSTITUTION_MENU_FLAGS) as InstitutionMenuFlagKey[];

/**
 * Regroupement pour la fiche établissement (admin) : facilite l’activation / désactivation
 * (ex. onglet INFPC à côté du pilotage RH).
 */
export type InstitutionMenuFlagAdminSection = { title: string; keys: readonly InstitutionMenuFlagKey[] };

const INSTITUTION_MENU_FLAG_ADMIN_GROUPS: readonly InstitutionMenuFlagAdminSection[] = [
  {
    title: 'Navigation & modules Formation / RH',
    keys: [
      INSTITUTION_MENU_FLAGS.institution_panel,
      INSTITUTION_MENU_FLAGS.integrations_page,
    ],
  },
  {
    title: 'Onglets — Contenu & formations',
    keys: [
      INSTITUTION_MENU_FLAGS.tab_flows,
      INSTITUTION_MENU_FLAGS.tab_webinar_settings,
      INSTITUTION_MENU_FLAGS.tab_trash,
      INSTITUTION_MENU_FLAGS.tab_supports,
      INSTITUTION_MENU_FLAGS.tab_categories,
    ],
  },
  {
    title: 'Onglets — Équipes & apprenants',
    keys: [
      INSTITUTION_MENU_FLAGS.tab_learners,
      INSTITUTION_MENU_FLAGS.tab_bi_studio,
      INSTITUTION_MENU_FLAGS.tab_groups,
    ],
  },
  {
    title: 'Onglets — Intégrations',
    keys: [
      INSTITUTION_MENU_FLAGS.tab_webhooks,
      INSTITUTION_MENU_FLAGS.tab_mcp,
    ],
  },
  {
    title: 'Onglets — Abonnement',
    keys: [INSTITUTION_MENU_FLAGS.tab_billing],
  },
] as const;

/** Sections pour l’UI admin (toutes les clés couvertes + filet de sécurité si nouvelle clé oubliée dans les groupes). */
export function getInstitutionMenuFlagsAdminSections(): InstitutionMenuFlagAdminSection[] {
  const seen = new Set<InstitutionMenuFlagKey>();
  const out: InstitutionMenuFlagAdminSection[] = [];
  for (const g of INSTITUTION_MENU_FLAG_ADMIN_GROUPS) {
    const keys = g.keys.filter((k) => {
      seen.add(k);
      return true;
    });
    out.push({ title: g.title, keys });
  }
  const missing = ALL_INSTITUTION_MENU_FLAG_KEYS.filter((k) => !seen.has(k));
  if (missing.length > 0) {
    out.push({ title: 'Autres (non classés)', keys: missing });
  }
  return out;
}

/** Libellés FR pour l’admin plateforme (fiche établissement). */
export const INSTITUTION_MENU_FLAG_LABELS_FR: Record<InstitutionMenuFlagKey, string> = {
  institution_panel: 'Menu : Panneau établissement',
  integrations_page: 'Page Intégrations (lien RH)',
  tab_webinar_settings: 'Onglet : Paramètres webinaires',
  tab_flows: 'Onglet : Parcours EduFlow',
  tab_trash: 'Onglet : Corbeille',
  tab_supports: 'Onglet : Supports',
  tab_categories: 'Onglet : Catégories',
  tab_learners: 'Onglet : Apprenants',
  tab_bi_studio: 'Onglet : Tableaux BI',
  tab_groups: 'Onglet : Groupes',
  tab_webhooks: 'Onglet : Webhooks',
  tab_mcp: 'Onglet : MCP',
  tab_billing: 'Onglet : Facturation / abonnement',
};

export function isInstitutionMenuEnabled(
  flags: Record<string, unknown> | null | undefined,
  key: string,
): boolean {
  if (flags == null || typeof flags !== 'object') return true;
  const v = flags[key];
  if (v === undefined) return true;
  return v !== false;
}

/** État complet pour l’admin : toutes les clés explicites (défaut activé). */
export function expandInstitutionMenuFlags(
  raw: Record<string, unknown> | null | undefined,
): Record<InstitutionMenuFlagKey, boolean> {
  const out = {} as Record<InstitutionMenuFlagKey, boolean>;
  for (const k of ALL_INSTITUTION_MENU_FLAG_KEYS) {
    out[k] = isInstitutionMenuEnabled(raw, k);
  }
  return out;
}

export function collapseInstitutionMenuFlags(
  expanded: Record<InstitutionMenuFlagKey, boolean>,
): Record<string, boolean> {
  const o: Record<string, boolean> = {};
  for (const k of ALL_INSTITUTION_MENU_FLAG_KEYS) {
    o[k] = expanded[k] !== false;
  }
  return o;
}
